import { useEffect, useRef } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { API_BASE_URL } from '@/api/client'
import { RealtimeApplicationToast } from '@/components/realtime-application-toast'

type InvalidatePayload = {
  eventId?: string
  applicationId?: number
  squareIds: number[]
  currentSquareIds: number[]
  refreshDashboard: boolean
  refreshApplicationDetail: boolean
}

type ApplicationCreatedPayload = {
  eventId?: string
  applicationId: number
  applicationNumber?: number
  path: string
  schemaName?: string
  squareIds: number[]
  squareNames: string[]
  stageName?: string
}

const DEDUPE_LIMIT = 200

function rememberEvent(
  seenEventIds: Set<string>,
  orderedEventIds: string[],
  eventId?: string,
) {
  if (!eventId) {
    return false
  }
  if (seenEventIds.has(eventId)) {
    return true
  }

  seenEventIds.add(eventId)
  orderedEventIds.push(eventId)
  if (orderedEventIds.length > DEDUPE_LIMIT) {
    const expiredEventId = orderedEventIds.shift()
    if (expiredEventId) {
      seenEventIds.delete(expiredEventId)
    }
  }
  return false
}

export function softRefreshCrmQueries(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['bootstrap'] }),
    queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
    queryClient.invalidateQueries({
      predicate: (query) => {
        const scope = query.queryKey[0]
        return (
          scope === 'square-detail' ||
          scope === 'current-square' ||
          scope === 'application-detail'
        )
      },
    }),
  ])
}

export function invalidateRealtimeQueries(
  queryClient: QueryClient,
  payload: InvalidatePayload,
) {
  const squareIds = new Set(payload.squareIds.map(String))
  const currentSquareIds = new Set(payload.currentSquareIds.map(String))
  const applicationId = payload.applicationId != null ? String(payload.applicationId) : null

  const invalidations: Array<Promise<unknown>> = []
  invalidations.push(queryClient.invalidateQueries({ queryKey: ['bootstrap'] }))
  if (payload.refreshDashboard) {
    invalidations.push(queryClient.invalidateQueries({ queryKey: ['dashboard'] }))
  }

  invalidations.push(
    queryClient.invalidateQueries({
      predicate: (query) => {
        const [scope, identifier] = query.queryKey

        if (scope === 'square-detail') {
          return squareIds.size === 0 || squareIds.has(String(identifier ?? ''))
        }
        if (scope === 'current-square') {
          return currentSquareIds.size === 0 || currentSquareIds.has(String(identifier ?? ''))
        }
        if (scope === 'application-detail') {
          if (!payload.refreshApplicationDetail) {
            return false
          }
          return applicationId === null || applicationId === String(identifier ?? '')
        }
        return false
      },
    }),
  )

  return Promise.all(invalidations)
}

export function CrmRealtimeListener() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const seenEventIdsRef = useRef(new Set<string>())
  const orderedEventIdsRef = useRef<string[]>([])

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.EventSource === 'undefined') {
      return undefined
    }

    const eventSource = new window.EventSource(`${API_BASE_URL}/realtime/stream`, {
      withCredentials: true,
    })

    eventSource.onopen = () => {
      void softRefreshCrmQueries(queryClient)
    }

    const handleInvalidate = (event: Event) => {
      const messageEvent = event as MessageEvent<string>
      let payload: InvalidatePayload
      try {
        payload = JSON.parse(messageEvent.data)
      } catch {
        return
      }

      if (
        rememberEvent(
          seenEventIdsRef.current,
          orderedEventIdsRef.current,
          payload.eventId,
        )
      ) {
        return
      }

      void invalidateRealtimeQueries(queryClient, payload)
    }

    const handleApplicationCreated = (event: Event) => {
      const messageEvent = event as MessageEvent<string>
      let payload: ApplicationCreatedPayload
      try {
        payload = JSON.parse(messageEvent.data)
      } catch {
        return
      }

      if (
        rememberEvent(
          seenEventIdsRef.current,
          orderedEventIdsRef.current,
          payload.eventId,
        )
      ) {
        return
      }

      toast.custom((toastId) => (
        <RealtimeApplicationToast
          applicationNumber={payload.applicationNumber ?? payload.applicationId}
          onClose={() => toast.dismiss(toastId)}
          onOpen={() => {
            toast.dismiss(toastId)
            navigate(payload.path)
          }}
          schemaName={payload.schemaName}
          squareNames={payload.squareNames}
          stageName={payload.stageName}
        />
      ), {
        className: 'bg-transparent border-none shadow-none p-0',
        duration: 12_000,
        unstyled: true,
      })
      void invalidateRealtimeQueries(queryClient, {
        applicationId: payload.applicationId,
        currentSquareIds: [],
        eventId: payload.eventId,
        refreshApplicationDetail: true,
        refreshDashboard: true,
        squareIds: payload.squareIds,
      })
    }

    eventSource.addEventListener('crm.invalidate', handleInvalidate)
    eventSource.addEventListener(
      'notification.application_created',
      handleApplicationCreated,
    )

    return () => {
      eventSource.removeEventListener('crm.invalidate', handleInvalidate)
      eventSource.removeEventListener(
        'notification.application_created',
        handleApplicationCreated,
      )
      eventSource.close()
    }
  }, [navigate, queryClient])

  return null
}
